import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppSelector } from '@/store/store-hooks'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { RootState } from '@/store/store'
import ErrorComponent from '@/components/error/error-component'
import ChatWindowDropZoneMultifile, { ChatWindowDropZoneMultifileRef } from '@/chat-common/components/ChatWindowDropZoneMultifile'
import ChatWindowUserFilesUpload, { ChatWindowUserFilesUploadRef } from '@/chat-common/components/ChatWindowUserFilesUpload'
import {
  selectAgentConversationExists,
  selectAgentConversationLoading,
  selectAgentConversationLoadError,
  selectAgentConversationMatterId,
} from '@/agent/conversations/store/selectors'
import AgentChatFormWrapper from '@/agent/chat_form/components/AgentChatFormWrapper'
import { FileUploadContext } from '@/context/file-upload-context'
import { useDispatch } from 'react-redux'
import { AgentConversation, AgentConversationsActions } from '@/agent/conversations/store/slice'
import EventBubbleList from '../events/components/EventBubbleList'
import ScrollToBottomIcon from './components/ScrollToBottomIcon'
import { selectAgentConversationHasEvents } from '../events/store/selectors'
import MessageFeedbackDialog from '@/chat-common/components/dialogs/MessageFeedbackDialog'
import { AgentResponseMetadataActions } from '../response_metadata/store/slice'
import { selectAgentResponseFeedbackDialog } from '../response_metadata/store/selectors'
import { PopoverContainerContext } from '@/context/popover-container-context'
import { ConversationNavigationActions } from '@/chat-common/conversation-navigation/store/slice'
import { setSelectedFilesContentExpanded } from '@/chat-common/store/chat-ux.slice'
import NewChatUX from './new_chat_ux/NewChatUX'
import MatterBoundInterception from '@/matter/components/MatterBoundInterception'
import { selectMatterById } from '@/matter/store/selectors'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import SelectActiveMatterPanel from '@/matter/components/SelectActiveMatterPanel'
import { canCreateChat } from '@/matter/utils/matter-permissions'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import {
  selectAgentConversationFormFilePaths,
  selectAgentConversationFormTemplateFilePaths,
  selectAgentConversationFormUserFilesRag,
} from '@/agent/chat_form/store/selectors'
import { shouldClearLegacyFileSelectionsForNewFilesUx } from '@/agent/chat_form/utils/legacy-file-cutover'
import { AgentConversationFormActions } from '@/agent/chat_form/store/slice'
import { selectArtifactById } from '@/artifacts/store/selectors'
import { buildArtifactViewerSession } from '@/artifacts/utils/build-artifact-viewer-session'
import { selectIsContentViewerOpen } from '@/content-viewer/store/selectors'
import { AuthContext } from '@/context/auth-context'
import ViewOnlyChatNotice from './components/ViewOnlyChatNotice'
import { useCreateNewAgentChat } from '@/agent/conversations/hooks/useNewAgentChat'
import { selectMatterMembers } from '@/collaboration/store/selectors'
import { getMemberDisplayName } from '@/collaboration/utils/members'

type AgentChatWindowProps = {
  conversationId: string
}

/**
 * Chat Window
 *
 * This component is mainly for templating the Chat Window area.
 * - Scrollable messages area
 * - Chat form at the bottom
 * @param props
 * @returns
 */
export default function AgentChatWindow(props: AgentChatWindowProps) {
  const { conversationId } = props

  const dispatch = useDispatch()
  const authContext = useContext(AuthContext)
  const { createNewAgentChat } = useCreateNewAgentChat()
  const [isCreatingOwnChat, setIsCreatingOwnChat] = useState(false)

  // Redux State Selectors
  const conversationExists = useAppSelector((state: RootState) => selectAgentConversationExists(state, { conversationId }))
  const conversation = useAppSelector((state: RootState) => state.agentConversationsState.conversations[conversationId])
  const conversationLoading = useAppSelector((state: RootState) => selectAgentConversationLoading(state, { conversationId: conversationId }))
  const conversationLoadError = useAppSelector((state: RootState) => selectAgentConversationLoadError(state, { conversationId: conversationId }))

  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)

  const conversationHasEvents = useAppSelector((state: RootState) => selectAgentConversationHasEvents(state, { conversationId: conversationId }))
  const feedbackDialog = useAppSelector((state: RootState) => selectAgentResponseFeedbackDialog(state))

  // Whether to show the loader
  const showLoader = conversationLoading && !conversationHasEvents

  // Whether to show the NewChatUX
  const showNewChatUX = !showLoader && !conversationHasEvents

  // Whether to show the MatterBoundInterception
  const conversationMatterId = useAppSelector((state: RootState) => selectAgentConversationMatterId(state, { conversationId }))
  const conversationMatter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: conversationMatterId }))
  const showMatterBoundInterception = conversationMatterId === null || isUnassignedMatter(conversationMatter)
  const currentUserUid = authContext?.user?.uid
  const showViewOnlyChat = !!conversationExists && !!conversationMatterId && !!currentUserUid && !!conversation && conversation.user_id !== currentUserUid
  const conversationMembers = useAppSelector((state: RootState) => selectMatterMembers(state, conversationMatterId ?? undefined))
  const ownerDisplayName = useMemo(() => {
    if (!conversation || !showViewOnlyChat) return null
    const ownerMember = conversationMembers.find((member) => member.user_id === conversation.user_id)
    return ownerMember ? getMemberDisplayName(ownerMember) : null
  }, [conversation, conversationMembers, showViewOnlyChat])
  const newFilesUxEnabled = kGetFeatureFlagStatus(kFeatureFlags.NEW_FILES_UX)
  const selectedLegacyFilePaths = useAppSelector((state: RootState) => selectAgentConversationFormFilePaths(state, { conversationId }))
  const selectedTemplatePaths = useAppSelector((state: RootState) => selectAgentConversationFormTemplateFilePaths(state, { conversationId }))
  const userFilesRag = useAppSelector((state: RootState) => selectAgentConversationFormUserFilesRag(state, { conversationId }))

  // Whether to prevent the user from starting a new chat if no matter is selected
  const disableNewChats = showNewChatUX && showMatterBoundInterception

  // ?artifactVersionId=<id> — an artifact version to open and scroll to on arrival
  const [searchParams, setSearchParams] = useSearchParams()
  const artifactVersionAnchorId = searchParams.get('artifactVersionId')
  const targetArtifactVersion = useAppSelector((state: RootState) => selectArtifactById(state, { artifactVersionId: artifactVersionAnchorId }))
  const isContentViewerOpen = useAppSelector((state: RootState) => selectIsContentViewerOpen(state))

  // Refs
  const scrollingChatWindowRef = useRef<HTMLDivElement | null>(null)
  const chatWindowDropZoneMultifileRef = useRef<ChatWindowDropZoneMultifileRef | null>(null)
  const chatWindowTemplateDropZoneMultifileRef = useRef<ChatWindowDropZoneMultifileRef | null>(null)
  const chatWindowUserFilesUploadRef = useRef<ChatWindowUserFilesUploadRef | null>(null)
  const chatWindowWritingSampleUploadRef = useRef<ChatWindowUserFilesUploadRef | null>(null)
  const openedArtifactVersionRef = useRef<string | null>(null)

  // Allows interaction with the child component's openSystemFileSelector method
  const openSystemFileSelector = () => {
    chatWindowDropZoneMultifileRef?.current?.openSystemFileSelector()
  }
  // Allows interaction with the child component's openSystemTemplateFileSelector method
  const openSystemTemplateFileSelector = () => {
    chatWindowTemplateDropZoneMultifileRef?.current?.openSystemFileSelector()
  }
  const openSystemUserFilesFileSelector = () => {
    chatWindowUserFilesUploadRef?.current?.openSystemFileSelector()
  }
  const openSystemWritingSampleFileSelector = () => {
    chatWindowWritingSampleUploadRef?.current?.openSystemFileSelector()
  }
  const addFilesToUserFilesUpload = (files: File[]) => {
    chatWindowUserFilesUploadRef?.current?.addFiles(files)
  }
  const cancelUserFileUpload = (clientTmpId: string) => {
    // The user-files and writing-sample instances each own their own Uppy/task ref, so the tmp id
    // lives in only one of them. cancelUpload is a no-op on the instance that doesn't own it, so
    // call both rather than tracking which target the upload belonged to.
    chatWindowUserFilesUploadRef?.current?.cancelUpload(clientTmpId)
    chatWindowWritingSampleUploadRef?.current?.cancelUpload(clientTmpId)
  }

  const userFilesUploadDisabledReason = (() => {
    if (!conversationMatterId) return 'Select a matter before uploading to Matter Files.'
    return undefined
  })()
  const userFilesUploadDisabled = !!userFilesUploadDisabledReason
  const userFilesUploadReady = newFilesUxEnabled && !userFilesUploadDisabled
  const showNewChatUploadDropZone = !newFilesUxEnabled || userFilesUploadReady

  const handleStartOwnChat = () => {
    const targetMatterId = conversationMatterId ?? activeMatterId
    if (!targetMatterId || isCreatingOwnChat) return

    setIsCreatingOwnChat(true)
    void createNewAgentChat(
      true,
      targetMatterId,
      () => {
        setIsCreatingOwnChat(false)
      },
      () => {
        setIsCreatingOwnChat(false)
      }
    )
  }

  /**
   * Load the conversation data for this chat
   */
  useEffect(() => {
    dispatch(AgentConversationsActions.fetchConversationData({ conversationId, forceRefresh: true }))
  }, [conversationId, dispatch])

  useEffect(() => {
    if (!newFilesUxEnabled || !conversationMatterId) return
    dispatch(UserFoldersActions.fetchRootFolder({ matterId: conversationMatterId }))
  }, [conversationMatterId, dispatch, newFilesUxEnabled])

  useEffect(() => {
    const shouldClearLegacyFileSelections = shouldClearLegacyFileSelectionsForNewFilesUx({
      newFilesUxEnabled,
      userFilesRag,
      filePaths: selectedLegacyFilePaths,
      templatePaths: selectedTemplatePaths,
    })

    if (!shouldClearLegacyFileSelections) return

    dispatch(AgentConversationFormActions.clearLegacyFilesForNewFilesCutover({ conversationId }))

    // Expand the files drawer to show the files reset notice inside it
    dispatch(setSelectedFilesContentExpanded({ conversationId, expanded: true }))
  }, [conversationId, dispatch, newFilesUxEnabled, selectedLegacyFilePaths, selectedTemplatePaths, userFilesRag])

  /**
   * Set as the last viewed conversation
   */
  useEffect(() => {
    if (conversationExists) {
      dispatch(
        ConversationNavigationActions.setLastViewedByMatter({
          conversation: conversation as AgentConversation,
          activeMatterId: activeMatterId,
        })
      )
    }
  }, [conversationExists, conversationId, dispatch, conversation, activeMatterId])

  /**
   * If the URL requested an artifact version (?artifactVersionId=), open it in the content viewer
   * once we have its record.
   */
  useEffect(() => {
    if (!artifactVersionAnchorId || !targetArtifactVersion) return
    // Guard so each requested artifact opens only once
    if (openedArtifactVersionRef.current === artifactVersionAnchorId) return
    openedArtifactVersionRef.current = artifactVersionAnchorId

    const session = buildArtifactViewerSession({ artifactVersion: targetArtifactVersion, conversationId })
    dispatch(AgentConversationsActions.setConversationActiveContentViewerSession({ conversationId, session }))
  }, [artifactVersionAnchorId, targetArtifactVersion, conversationId, dispatch])

  /**
   * Scroll the requested artifact version into view.
   *
   * Waits for the events to be in the DOM (so the anchor exists) and for the content viewer to be
   * open: opening it reflows the chat column to its split width, which moves the anchor, so scrolling
   * any earlier lands short of it.
   */
  useEffect(() => {
    if (!artifactVersionAnchorId || !conversationHasEvents || !isContentViewerOpen) return
    const anchor = scrollingChatWindowRef.current?.querySelector(`[data-artifact-version-anchor="${CSS.escape(artifactVersionAnchorId)}"]`)
    anchor?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setSearchParams(
      (prev) => {
        prev.delete('artifactVersionId')
        return prev
      },
      { replace: true }
    )
  }, [conversationHasEvents, isContentViewerOpen, artifactVersionAnchorId, setSearchParams])

  // If there was an error loading the conversation, show the error component
  if (conversationLoadError) {
    return (
      <div className={'p-5'}>
        <ErrorComponent {...conversationLoadError} />
      </div>
    )
  }

  return (
    <>
      {feedbackDialog.visible && feedbackDialog.eventId && feedbackDialog.positive !== null && (
        <MessageFeedbackDialog
          conversationId={conversationId}
          eventId={feedbackDialog.eventId}
          positive={feedbackDialog.positive}
          visible={feedbackDialog.visible}
          onClose={() => dispatch(AgentResponseMetadataActions.closeFeedbackDialog())}
        />
      )}

      {/* Provide the container element to the popover context, used for popover collision boundaries */}
      <PopoverContainerContext.Provider value={scrollingChatWindowRef.current}>
        <div className="flex w-full min-w-0 flex-grow flex-col items-center px-2">
          <div ref={scrollingChatWindowRef} className="w-full min-w-0 flex-1 flex-grow overflow-x-hidden overflow-y-scroll">
            {/* This div will contain the conversation and will be scrollable */}
            <div className="mx-auto h-full w-full min-w-0 max-w-3xl">
              {/* Render circular progress indicator if we're loading the messages and there are no messages */}
              {showLoader && (
                <div className={'w-full h-full flex justify-center items-center'}>
                  <CircularProgressContinuousSized size={30} thickness={7} hexColor={'#2A49FF'} />
                </div>
              )}

              {/* New Chat UX:
                Renders the new chat intro and shows the regular file drop zone UI for initial uploads.
                The template drop zone is hidden but mounted, enabling opening the system's file selector for template files via the "Apply Template" File Action Menu.
              */}
              {showNewChatUX &&
                (disableNewChats ? (
                  <SelectActiveMatterPanel
                    canSelectMatter={canCreateChat}
                    description="Select a Matter to draft, analyze, and keep your work in the correct case."
                    title="What do you want to get done today?"
                  />
                ) : (
                  <NewChatUX
                    conversationId={conversationId}
                    chatWindowDropZoneMultifileRef={chatWindowDropZoneMultifileRef}
                    chatWindowTemplateDropZoneMultifileRef={chatWindowTemplateDropZoneMultifileRef}
                    onFilesAccepted={userFilesUploadReady ? addFilesToUserFilesUpload : undefined}
                    showVisibleDropZone={showNewChatUploadDropZone}
                  />
                ))}

              {/* Active Conversation:
                Both drop zones are hidden but mounted once the conversation has started. They exist only to support
                triggering the system's file selectors (one for regular files, one for templates) via the File Action Menus.
              */}
              {conversationExists && conversationHasEvents && (
                <>
                  <ChatWindowDropZoneMultifile ref={chatWindowDropZoneMultifileRef} hideDropZoneUI={conversationHasEvents} chatId={conversationId} />
                  <ChatWindowDropZoneMultifile
                    ref={chatWindowTemplateDropZoneMultifileRef}
                    hideDropZoneUI={true}
                    chatId={conversationId}
                    isSingleFileLimit={true}
                    isTemplateMode={true}
                  />
                </>
              )}

              {newFilesUxEnabled && conversationExists && !showMatterBoundInterception && !showViewOnlyChat && (
                <>
                  <ChatWindowUserFilesUpload ref={chatWindowUserFilesUploadRef} conversationId={conversationId} matterId={conversationMatterId} />
                  <ChatWindowUserFilesUpload
                    ref={chatWindowWritingSampleUploadRef}
                    conversationId={conversationId}
                    matterId={conversationMatterId}
                    uploadTarget="writing-sample"
                  />
                </>
              )}

              <EventBubbleList conversationId={conversationId} />
            </div>
          </div>

          {/* This form will stay at the bottom of the screen */}
          {conversationExists && !disableNewChats && (
            // #FAF9F5 is the hex code to match the background gradient - this is just a temporary solution until we get to ENG-5654
            <div className={'w-full pb-4 z-10 bg-[#FAF9F5]'}>
              {/* Floating go to bottom button */}
              <ScrollToBottomIcon conversationId={conversationId} scrollingDivRef={scrollingChatWindowRef} />
              <FileUploadContext.Provider
                value={{
                  openSystemFileSelector,
                  openSystemTemplateFileSelector,
                  openSystemUserFilesFileSelector,
                  openSystemWritingSampleFileSelector,
                  cancelUserFileUpload,
                  userFilesUploadDisabled,
                  userFilesUploadDisabledReason,
                }}
              >
                {/* Show the MatterBoundInterception if the conversation is not assigned to a matter, otherwise show the AgentChatFormWrapper */}
                {showMatterBoundInterception ? (
                  <MatterBoundInterception conversationId={conversationId} />
                ) : showViewOnlyChat ? (
                  <ViewOnlyChatNotice ownerDisplayName={ownerDisplayName} disabled={isCreatingOwnChat} onStartOwnChat={handleStartOwnChat} />
                ) : (
                  <AgentChatFormWrapper conversationId={conversationId} />
                )}
              </FileUploadContext.Provider>
            </div>
          )}
        </div>
      </PopoverContainerContext.Provider>
    </>
  )
}
